import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { CommonType } from '@app/domain/model/CommonType';
import { Select } from '@app/app/_components/Input/Select/Select';

interface Props {
    formState: FormState<PurchaseHomeInsuranceResult>;
    leasings: ProductDataLeasing[];
    pledgedToBank: Nullable<string>;
    handlePledgedToBankChange: (value: string) => void;
    selectedLeasing: Nullable<string>;
    handleSelectedLeasingChange: (value: string) => void;
}

export function PledgedToBankPanel({
    formState,
    leasings,
    pledgedToBank,
    handlePledgedToBankChange,
    selectedLeasing,
    handleSelectedLeasingChange,
}: Props) {
    const t = useTranslations();

    const pledgedOptions = [
        { label: t('general.yes'), value: CommonType.Yes },
        { label: t('general.no'), value: CommonType.No },
    ];

    const leasingsOptions = useMemo(() => {
        if (!leasings || leasings.length === 0) {
            return [];
        }
        return leasings.map(leasing => ({
            label: leasing.name,
            value: leasing.code,
        }));
    }, [leasings]);

    return (
        <CollapsablePanel label={t('eshop.home.summary.pledgedToBank')} collapsable={false}>
            <div className="flex flex-col gap-4 px-6 pt-2">
                <Select
                    options={pledgedOptions}
                    value={pledgedToBank ?? ''}
                    error={formState.violations?.pledgedToBank}
                    name="pledgedToBank"
                    onChange={handlePledgedToBankChange}
                    label={t('eshop.home.objectSummary.answer')}
                    placeholder={t('eshop.general.select')}
                />
                {pledgedToBank === CommonType.Yes && leasings.length > 0 && (
                    <Select
                        options={leasingsOptions}
                        label={t('eshop.home.objectSummary.leasing')}
                        error={formState.violations?.leasing}
                        name="leasing"
                        value={selectedLeasing ?? ''}
                        onChange={handleSelectedLeasingChange}
                        placeholder={t('eshop.general.select')}
                    />
                )}
            </div>
        </CollapsablePanel>
    );
}
